import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { baseStyles } from '../styles/shared.js';

// ---------------------------------------------------------------------------
// <sysml-tab-bar> — Horizontal scrollable editor tab strip
//
// Each tab represents an open document. Preview tabs show italic text and are
// replaced when another document is single-clicked. Pinned (non-preview) tabs
// persist until explicitly closed.
//
// Events: @tab-select, @tab-close, @tab-pin, @tab-reorder
// ---------------------------------------------------------------------------

export interface TabInfo {
  id: string;
  name: string;
  isDirty: boolean;
  isPreview: boolean;
}

@customElement('sysml-tab-bar')
export class SysmlTabBar extends LitElement {
  static override styles = [
    baseStyles,
    css`
      :host {
        display: flex;
        align-items: stretch;
        height: 32px;
        flex-shrink: 0;
        border-bottom: 1px solid var(--r5-border);
        background: var(--r5-surface-muted);
        overflow-x: auto;
        overflow-y: hidden;
        scrollbar-width: thin;
      }
      :host::-webkit-scrollbar {
        height: 3px;
      }
      :host::-webkit-scrollbar-thumb {
        background: color-mix(in srgb, var(--r5-text) 15%, transparent);
        border-radius: 2px;
      }

      .tab {
        display: flex;
        align-items: center;
        gap: var(--r5-space-1);
        padding: 0 var(--r5-space-2);
        min-width: 80px;
        max-width: 180px;
        border: none;
        border-right: 1px solid var(--r5-border);
        background: none;
        color: var(--r5-text-muted);
        font-family: inherit;
        font-size: var(--r5-text-xs);
        cursor: pointer;
        white-space: nowrap;
        position: relative;
        user-select: none;
        transition: background var(--r5-duration-fast) var(--r5-ease-default);
      }
      .tab:hover {
        background: color-mix(in srgb, var(--r5-text) 5%, transparent);
      }
      .tab.active {
        background: var(--r5-surface);
        color: var(--r5-text);
      }
      .tab.active::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: var(--r5-primary);
      }
      .tab.preview .tab-name {
        font-style: italic;
      }
      .tab.dragging {
        opacity: 0.5;
      }
      .tab.drag-over {
        background: color-mix(in srgb, var(--r5-primary) 10%, transparent);
      }

      .tab-name {
        overflow: hidden;
        text-overflow: ellipsis;
        flex: 1;
        min-width: 0;
        text-align: left;
      }

      .dirty-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--r5-warning-500);
        flex-shrink: 0;
      }

      .close-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        border: none;
        border-radius: var(--r5-radius-sm);
        background: none;
        color: var(--r5-text-muted);
        cursor: pointer;
        flex-shrink: 0;
        opacity: 0;
        transition: opacity var(--r5-duration-fast) var(--r5-ease-default);
        padding: 0;
      }
      .tab:hover .close-btn,
      .tab.active .close-btn {
        opacity: 1;
      }
      .close-btn:hover {
        color: var(--r5-text);
        background: color-mix(in srgb, var(--r5-text) 12%, transparent);
      }
    `,
  ];

  @property({ type: Array }) tabs: TabInfo[] = [];
  @property({ attribute: 'active-tab-id' }) activeTabId: string | null = null;

  private _dragSourceId: string | null = null;
  private _dragOverId: string | null = null;

  override render() {
    if (this.tabs.length === 0) return nothing;

    return html`<div role="tablist" aria-label="Open editors">${this.tabs.map(
      (tab) => html`
        <div
          role="tab"
          aria-selected=${tab.id === this.activeTabId ? 'true' : 'false'}
          class="tab ${tab.id === this.activeTabId ? 'active' : ''} ${tab.isPreview ? 'preview' : ''} ${this._dragSourceId === tab.id ? 'dragging' : ''} ${this._dragOverId === tab.id ? 'drag-over' : ''}"
          draggable="true"
          @click=${() => this._onSelect(tab.id)}
          @dblclick=${() => this._onPin(tab.id)}
          @auxclick=${(e: MouseEvent) => this._onAuxClick(e, tab.id)}
          @dragstart=${(e: DragEvent) => this._onDragStart(e, tab.id)}
          @dragover=${(e: DragEvent) => this._onDragOver(e, tab.id)}
          @dragleave=${() => this._onDragLeave()}
          @drop=${(e: DragEvent) => this._onDrop(e, tab.id)}
          @dragend=${() => this._onDragEnd()}
        >
          <span class="tab-name">${tab.name}</span>
          ${tab.isDirty ? html`<span class="dirty-dot"></span>` : nothing}
          <button
            class="close-btn"
            @click=${(e: Event) => { e.stopPropagation(); this._onClose(tab.id); }}
            title="Close"
          >
            <ark-icon name="x" size="xs"></ark-icon>
          </button>
        </div>
      `,
    )}</div>`;
  }

  // -- Events -----------------------------------------------------------------

  private _onSelect(id: string): void {
    this.dispatchEvent(
      new CustomEvent('tab-select', { detail: { id }, bubbles: true, composed: true }),
    );
  }

  private _onClose(id: string): void {
    this.dispatchEvent(
      new CustomEvent('tab-close', { detail: { id }, bubbles: true, composed: true }),
    );
  }

  private _onPin(id: string): void {
    this.dispatchEvent(
      new CustomEvent('tab-pin', { detail: { id }, bubbles: true, composed: true }),
    );
  }

  /** Middle-click to close */
  private _onAuxClick(e: MouseEvent, id: string): void {
    if (e.button === 1) {
      e.preventDefault();
      this._onClose(id);
    }
  }

  // -- Drag and drop reorder --------------------------------------------------

  private _onDragStart(e: DragEvent, id: string): void {
    this._dragSourceId = id;
    e.dataTransfer?.setData('text/plain', id);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
    }
    this.requestUpdate();
  }

  private _onDragOver(e: DragEvent, id: string): void {
    if (!this._dragSourceId || this._dragSourceId === id) return;
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
    this._dragOverId = id;
    this.requestUpdate();
  }

  private _onDragLeave(): void {
    this._dragOverId = null;
    this.requestUpdate();
  }

  private _onDrop(e: DragEvent, targetId: string): void {
    e.preventDefault();
    const sourceId = this._dragSourceId;
    this._dragSourceId = null;
    this._dragOverId = null;
    this.requestUpdate();

    if (!sourceId || sourceId === targetId) return;

    this.dispatchEvent(
      new CustomEvent('tab-reorder', {
        detail: { sourceId, targetId },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _onDragEnd(): void {
    this._dragSourceId = null;
    this._dragOverId = null;
    this.requestUpdate();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-tab-bar': SysmlTabBar;
  }
}
